import { z } from 'zod';
import { JobStatus, stageForJobStatus, type GenerationStage } from '../enums';

/**
 * Джобы генерации (§44, §68).
 *
 * Наружу отдаётся продуктовый вид: три этапа §68, прогресс и текст ошибки.
 * Ни провайдера, ни retryCount, ни технических кодов (§41, §45).
 */
export interface JobView {
  id: string;
  operation: string;
  status: JobStatus;
  /** Один из трёх этапов §68 либо null для терминальных статусов. */
  stage: GenerationStage | null;
  stageLabel: string | null;
  progress: number;
  unitsReserved: number;
  unitsSpent: number;
  /** Продуктовый код §52 — фронт по нему берёт текст. */
  errorCode: string | null;
  projectId: string | null;
  sceneId: string | null;
  result: Record<string, unknown> | null;
  createdAt: string;
  completedAt: string | null;
}

export const isTerminal = (status: JobStatus): boolean =>
  status === JobStatus.COMPLETED || status === JobStatus.FAILED || status === JobStatus.CANCELLED;

export const isActive = (status: JobStatus): boolean => !isTerminal(status);

export { stageForJobStatus };

export const jobsQuerySchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    /** Только незавершённые — то, что показывается в интерфейсе как «в работе». */
    activeOnly: z
      .union([z.boolean(), z.enum(['true', 'false'])])
      .default(false)
      .transform((value) => value === true || value === 'true'),
  })
  .strict();
export type JobsQuery = z.infer<typeof jobsQuerySchema>;

/**
 * Насколько часто фронту спрашивать статус.
 *
 * Интервал растёт с возрастом джобы: создание аватара идёт часами, и долбить
 * сервер раз в секунду всё это время незачем (agents/api-jobs-queues.md §8).
 */
export function pollIntervalMs(job: Pick<JobView, 'status' | 'createdAt'>): number | false {
  if (isTerminal(job.status)) return false;
  const ageSec = (Date.now() - new Date(job.createdAt).getTime()) / 1000;
  if (ageSec < 30) return 1500;
  if (ageSec < 120) return 3000;
  if (ageSec < 600) return 10_000;
  return 30_000;
}
